import React, { useState, useEffect } from "react";
import { clarity } from "react-microsoft-clarity";
import { TokenHelper } from "../shared/TokenHelper";
import TokenRefresher from "../shared/TokenRefresher";
import { JwtManager } from "../shared/JwtManager";
import { useAuthSessionOrchestrator } from "../auth/AuthSessionOrchestrator";
import { Operation, Entity } from "../shared/interfaces/ClaimsControl";
import { UserRoles } from "../usersManagement/UserRolesEnum";
import { userRoleStringRemoveSpaceAndCaptialiseFirstLetter } from "../usersManagement/utilities";
import { UserRolesConstants } from "../shared/constants/UserRolesConstants";
import { useQueryClient } from "react-query";

export interface IAuthContext {
  userIsAuthenticated: boolean;
  setUserIsAuthenticated?: (authenticated?: boolean) => void;
  setUserIsNotAuthenticated?: () => void;
  checkTokenIsValid?: () => Promise<boolean>;
  logout: () => void | Promise<void>;
  canI: (
    operation: Operation,
    entity: Entity,
    customerId?: number,
    companyId?: number,
    siteId?: number,
  ) => boolean | undefined;
  amIEaEmployee: () => boolean | undefined;
}

export const AuthContext = React.createContext({} as IAuthContext);

const checkTokenInterval = 60000;
const operations = ["Delete", "Add", "Edit", "View"];
const userRolePrefix = ["Customer", "Company", "Site"];
const tokenHelper = new TokenHelper({});
const tokenRefresher = new TokenRefresher({});
const tokenManager = new JwtManager({});

const EA_EMAIL_DOMAINS = ["@eatechnology.com", "@eatechnology.sg", "@eatechnologyusa.com"];

export function AuthContextProvider(props: React.PropsWithChildren<unknown>) {
  const [userIsAuthenticated, setUserIsAuthenticated] = useState(false);
  const queryClient = useQueryClient();
  const { endSession, stage } = useAuthSessionOrchestrator();
  const auth0SessionIsAuthenticated = stage.status === "authenticated";

  useEffect(() => {
    function updateTokenValidity() {
      checkTokenIsValid().then((tokenIsValid) => {
        if (tokenIsValid) setUserIsAuthenticated(true);
        else setUserIsAuthenticated(false);
      });
    }

    if (!tokenHelper.tokenIsValid()) setUserIsAuthenticated(false);
    else setUserIsAuthenticated(true);
    setInterval(updateTokenValidity, checkTokenInterval);
  }, []);

  async function checkTokenIsValid(): Promise<boolean> {
    if (tokenHelper.tokenIsValid()) {
      return Promise.resolve(true);
    }

    // Try refreshing if a refresh token is available
    if (!tokenHelper.refreshTokenExists()) {
      return Promise.resolve(false);
    }

    return tokenRefresher
      .refreshToken()
      .then(() => tokenHelper.tokenIsValid())
      .catch(() => false);
  }

  function handleUserAuthentication() {
    setUserIsAuthenticated(true);

    // Identify user in Clarity for analytics
    if (clarity.hasStarted()) {
      clarity.consent();

      const username = tokenHelper.getUsername();
      if (username) {
        clarity.identify(username, {});
      }
    }
  }

  async function logout() {
    await endSession();
    queryClient.removeQueries();
    queryClient.clear();
    setUserIsAuthenticated(false);
  }

  function canI(
    operation: Operation,
    entity: Entity,
    customerId?: number,
    companyId?: number,
    siteId?: number,
  ): boolean | undefined {
    const tokenPayload = tokenManager.getPayload();
    if (!tokenPayload) return false;

    if (tokenPayload.IsAdmin === "IsAdmin") return true;

    if (entity !== "customerSettings" || operation !== "add")
      if (tokenPayload.ManagedSurveysSuperAdmin === "ManagedSurveysSuperAdmin") return true;

    if (entity === "superAdmin" && operation === "add") {
      return tokenPayload.ManagedSurveysSuperAdmin === "ManagedSurveysSuperAdmin";
    }

    if (customerId && entity === "customer" && operation === "edit") {
      return tokenPayload.CustomerUTPAdmin?.includes(customerId.toString());
    }

    if (customerId && entity === "customer" && operation === "view") {
      if (checkUserRoleExists(tokenPayload)) return true;
      return operations.some((op) => tokenPayload[`Customer${op}`]?.includes(customerId.toString()));
    }

    if (entity === "results") {
      switch (operation) {
        case "view":
          return true;
        case "add":
          return (
            (tokenPayload.SubmitUTP2Data === "SubmitUTP2Data" ||
              canAddOrEditResultForCustomer(tokenPayload, customerId) ||
              canAddOrEditResultForCompany(tokenPayload, companyId) ||
              canAddOrEditResultForSite(tokenPayload, siteId)) ??
            false
          );
      }
    }

    if (entity === "resultComments") {
      switch (operation) {
        case "view":
          return true;
        case "add":
        case "edit":
          return (
            (tokenPayload.SubmitUTP2Data === "SubmitUTP2Data" ||
              canAddOrEditResultForCustomer(tokenPayload, customerId) ||
              canAddOrEditResultForCompany(tokenPayload, companyId) ||
              canAddOrEditResultForSite(tokenPayload, siteId)) ??
            false
          );
      }
    }

    if (entity === "reportsList" && operation === "view") {
      return (
        tokenPayload.CustomerStandardUser?.valueOf() !== undefined ||
        tokenPayload.CustomerExpertUser?.valueOf() !== undefined ||
        tokenPayload.CustomerUTPAdmin?.valueOf() !== undefined ||
        tokenPayload.CustomerView?.valueOf() !== undefined ||
        tokenPayload.CompanyStandardUser?.valueOf() !== undefined ||
        tokenPayload.CompanyExpertUser?.valueOf() !== undefined ||
        tokenPayload.SiteStandardUser?.valueOf() !== undefined ||
        tokenPayload.SiteExpertUser?.valueOf() !== undefined
      );
    }

    if (entity === "reports") {
      switch (operation) {
        case "view":
          return (
            (canViewReportsForCustomer(tokenPayload, customerId) ||
              canViewReportsForCompany(tokenPayload, companyId) ||
              canViewReportsForSite(tokenPayload, siteId)) ??
            false
          );
        case "add":
        case "edit":
          return (
            (canAddOrEditReportsForCustomer(tokenPayload, customerId) ||
              canAddOrEditReportsForCompany(tokenPayload, companyId) ||
              canAddOrEditReportsForSite(tokenPayload, siteId)) ??
            false
          );
        case "delete":
          return canDeleteReportsForCustomer(tokenPayload, customerId) ?? false;
      }
    }

    if (entity === "hvSubstations" && (operation === "add" || operation === "edit")) {
      return canAddOrEditHvSubstationAtCustomerLevel(tokenPayload, customerId) ?? false;
    }

    if (entity === "companies" || entity === "sites") {
      if (tokenPayload.CustomerUTPAdmin) return true;
    }

    if (entity === "customerSettings") {
      switch (operation) {
        // This is the only feature managedSurveysSuperAdmins do not automatically have access to as the backend endpoint auth only allows IsAdmins and UserAdmins only.
        case "add":
          if (tokenPayload.IsUserAdmin) return true;
          break;

        default:
          if (tokenPayload.CustomerUTPAdmin) return true;
      }
    }

    if (entity === "users") {
      return !!tokenPayload.CustomerUTPAdmin;
    }
  }

  function generateRoleClaim(prefix: string, role: string): string {
    let roleString = `${prefix}${userRoleStringRemoveSpaceAndCaptialiseFirstLetter(role)}`;

    if (roleString === UserRolesConstants.CustomerAdminUser) roleString = UserRolesConstants.CustomerUTPAdmin;

    return roleString;
  }

  function checkUserRoleExists(tokenPayload: Record<string, string | string[] | undefined>) {
    for (let prefix of userRolePrefix) {
      for (let role of Object.values(UserRoles)) {
        let roleString = generateRoleClaim(prefix, role);
        if (tokenPayload[roleString]) return true;
      }
    }
  }

  function canAddOrEditResultForCustomer(
    tokenPayload: Record<string, string | string[] | undefined>,
    customerId?: number,
  ) {
    return (
      customerId !== undefined &&
      customerId !== 0 &&
      (tokenPayload.CustomerStandardUser?.includes(customerId.toString()) ||
        tokenPayload.CustomerExpertUser?.includes(customerId.toString()) ||
        tokenPayload.CustomerUTPAdmin?.includes(customerId.toString()))
    );
  }

  function canAddOrEditResultForCompany(
    tokenPayload: Record<string, string | string[] | undefined>,
    companyId?: number,
  ) {
    return (
      companyId !== undefined &&
      companyId !== 0 &&
      (tokenPayload.CompanyStandardUser?.includes(companyId.toString()) ||
        tokenPayload.CompanyExpertUser?.includes(companyId.toString()))
    );
  }

  function canAddOrEditResultForSite(tokenPayload: Record<string, string | string[] | undefined>, siteId?: number) {
    return (
      siteId !== undefined &&
      siteId !== 0 &&
      (tokenPayload.SiteStandardUser?.includes(siteId.toString()) ||
        tokenPayload.SiteExpertUser?.includes(siteId.toString()))
    );
  }

  function canViewReportsForCustomer(tokenPayload: Record<string, string | string[] | undefined>, customerId?: number) {
    return (
      customerId !== undefined &&
      customerId !== 0 &&
      (tokenPayload.CustomerStandardUser?.includes(customerId.toString()) ||
        tokenPayload.CustomerExpertUser?.includes(customerId.toString()) ||
        tokenPayload.CustomerUTPAdmin?.includes(customerId.toString()) ||
        tokenPayload.CustomerView?.includes(customerId.toString()))
    );
  }

  function canViewReportsForCompany(tokenPayload: Record<string, string | string[] | undefined>, companyId?: number) {
    return (
      companyId !== undefined &&
      companyId !== 0 &&
      (tokenPayload.CompanyStandardUser?.includes(companyId.toString()) ||
        tokenPayload.CompanyExpertUser?.includes(companyId.toString()))
    );
  }

  function canViewReportsForSite(tokenPayload: Record<string, string | string[] | undefined>, siteId?: number) {
    return (
      siteId !== undefined &&
      siteId !== 0 &&
      (tokenPayload.SiteStandardUser?.includes(siteId.toString()) ||
        tokenPayload.SiteExpertUser?.includes(siteId.toString()))
    );
  }

  function canAddOrEditReportsForCustomer(
    tokenPayload: Record<string, string | string[] | undefined>,
    customerId?: number,
  ) {
    return (
      customerId !== undefined &&
      customerId !== 0 &&
      (tokenPayload.CustomerExpertUser?.includes(customerId.toString()) ||
        tokenPayload.CustomerUTPAdmin?.includes(customerId.toString()) ||
        tokenPayload.CustomerAdd?.includes(customerId.toString()) ||
        tokenPayload.CustomerEdit?.includes(customerId.toString()))
    );
  }

  function canAddOrEditReportsForCompany(
    tokenPayload: Record<string, string | string[] | undefined>,
    companyId?: number,
  ) {
    return companyId !== undefined && companyId !== 0 && tokenPayload.CompanyExpertUser?.includes(companyId.toString());
  }

  function canAddOrEditReportsForSite(tokenPayload: Record<string, string | string[] | undefined>, siteId?: number) {
    return siteId !== undefined && siteId !== 0 && tokenPayload.SiteExpertUser?.includes(siteId.toString());
  }

  function canDeleteReportsForCustomer(
    tokenPayload: Record<string, string | string[] | undefined>,
    customerId?: number,
  ) {
    return (
      customerId !== undefined && customerId !== 0 && tokenPayload.CustomerUTPAdmin?.includes(customerId.toString())
    );
  }

  function canAddOrEditHvSubstationAtCustomerLevel(
    tokenPayload: Record<string, string | string[] | undefined>,
    customerId?: number,
  ) {
    return (
      customerId !== undefined &&
      customerId !== 0 &&
      (tokenPayload.CustomerUTPAdmin?.includes(customerId.toString()) ||
        tokenPayload.CustomerExpertUser?.includes(customerId.toString()) ||
        tokenPayload.CustomerStandardUser?.includes(customerId.toString()) ||
        tokenPayload.CustomerAdd?.includes(customerId.toString()) ||
        tokenPayload.CustomerEdit?.includes(customerId.toString()))
    );
  }

  function amIEaEmployee(): boolean | undefined {
    const tokenPayload = tokenManager.getPayload();
    const email = tokenPayload?.Email;

    if (email) return EA_EMAIL_DOMAINS.some((domain) => email?.toString().endsWith(domain));

    return false;
  }

  return (
    <AuthContext.Provider
      value={{
        userIsAuthenticated: userIsAuthenticated || auth0SessionIsAuthenticated,
        setUserIsAuthenticated: () => handleUserAuthentication(),
        setUserIsNotAuthenticated: () => setUserIsAuthenticated(false),
        checkTokenIsValid: checkTokenIsValid,
        logout: logout,
        canI: canI,
        amIEaEmployee: amIEaEmployee,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
}
